import React from 'react';
import { TrendingUp } from 'lucide-react';
import type { SubmarketScore } from '../../types/calculations';
import { GoldBadge } from '../Luxury';

interface ZandiDataCardProps {
  zandiScoring: SubmarketScore;
  zandiProjection?: {
    submarket_phase?: string;
    baseline_irr_5yr?: number;
    adjusted_irr_5yr?: number;
  };
  className?: string;
}

const FACTOR_LABELS: Record<string, string> = {
  rent_gap_score: 'Rent Gap Opportunity',
  mobi_score: 'Affordability (MOBI)',
  momentum_score: 'Market Momentum',
  vacancy_score: 'Vacancy Pressure',
  valuation_score: 'Valuation Signal',
  permit_score: 'Permit Activity',
  spillover_score: 'Spillover Potential',
};

const FACTOR_KEYS = Object.keys(FACTOR_LABELS);

interface FactorEntry {
  key: string;
  label: string;
  score: number;
  deviation: number;
}

function getTopFactors(scoring: SubmarketScore, n = 3): FactorEntry[] {
  return FACTOR_KEYS
    .map((key) => ({
      key,
      label: FACTOR_LABELS[key],
      score: (scoring as unknown as Record<string, number>)[key],
      deviation: Math.abs((scoring as unknown as Record<string, number>)[key] - 50),
    }))
    .filter((f) => !isNaN(f.score) && f.score !== undefined)
    .sort((a, b) => b.deviation - a.deviation)
    .slice(0, n);
}

function getBarColor(score: number): string {
  if (score > 60) return 'bg-emerald-500';
  if (score < 40) return 'bg-red-600';
  return 'bg-gold';
}

/**
 * ZandiDataCard
 *
 * Market context at a glance — shows composite score + top 3 highest-impact
 * factors for the submarket. Designed to sit above DealNarrativeCard.
 *
 * Renders null when zandiScoring is invalid.
 */
export const ZandiDataCard: React.FC<ZandiDataCardProps> = ({
  zandiScoring,
  zandiProjection,
  className = '',
}) => {
  // Null guard: must have a valid composite score
  if (!zandiScoring || zandiScoring.composite_score === undefined || zandiScoring.composite_score === null) {
    return null;
  }

  const topFactors = getTopFactors(zandiScoring);
  const composite = Math.round(zandiScoring.composite_score);
  const rank = zandiScoring.rank;
  const submarketName = zandiScoring.name || 'Unknown Submarket';

  // CAGR from raw_data
  const rawData = zandiScoring.raw_data;
  const cagr = rawData?.egi_cagr_5yr ?? rawData?.cagr_5yr;
  const phase = zandiProjection?.submarket_phase;

  return (
    <div className={`bg-obsidian rounded-lg border border-slate-dark p-4 ${className}`}>
      {/* Header Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-gold" />
          <span className="text-sm font-semibold text-ivory">{submarketName}</span>
        </div>
        <div className="flex items-center gap-2">
          {rank !== undefined && rank !== null && (
            <span className="text-xs text-stone-400 font-mono">#{rank} of 29</span>
          )}
          <GoldBadge variant="gold">{composite}/100</GoldBadge>
        </div>
      </div>

      {/* Top 3 Factor Bars */}
      {topFactors.length > 0 && (
        <div className="space-y-2 mb-3">
          {topFactors.map((factor) => (
            <div key={factor.key}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs text-ivory/80">{factor.label}</span>
                <span className="text-xs font-mono text-ivory/80">{Math.round(factor.score)}</span>
              </div>
              {/* Score bar track */}
              <div className="w-full h-1.5 rounded-full bg-charcoal/40 overflow-hidden">
                <div
                  className={`h-full rounded-full ${getBarColor(factor.score)} transition-all`}
                  style={{ width: `${Math.min(100, Math.max(0, factor.score))}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer - CAGR + Phase */}
      {(cagr !== undefined && cagr !== null) || phase ? (
        <div className="text-xs text-stone-400 border-t border-slate-dark pt-2 flex flex-wrap gap-x-3 gap-y-0.5">
          {cagr !== undefined && cagr !== null && (
            <span>5Y CAGR: {(cagr * 100).toFixed(1)}%</span>
          )}
          {phase && <span>Phase: {phase}</span>}
        </div>
      ) : null}
    </div>
  );
};

export default ZandiDataCard;
